import type { Link, User } from './api';
import type { PastelColor } from '../components/c2/Pill';

const PASTELS: PastelColor[] = ['peach', 'mint', 'sky', 'lemon', 'lilac'];

export function colorForString(s: string): PastelColor {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return PASTELS[Math.abs(h) % PASTELS.length];
}

export function nameFromEmail(email: string | null | undefined): string {
  if (!email) return 'Unknown';
  const local = email.split('@')[0] ?? '';
  if (!local) return email;
  return local
    .split(/[.\-_]+/)
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

export function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]![0] ?? '') + (parts[1]![0] ?? '')).toUpperCase();
}

/** Parse SQLite "YYYY-MM-DD HH:MM:SS" as UTC, plus normal ISO strings. */
export function parseTimestamp(s: string): number {
  if (!s) return NaN;
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(s) && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(s)) {
    return new Date(s.replace(' ', 'T') + 'Z').getTime();
  }
  return new Date(s).getTime();
}

export function relativeTime(iso: string): string {
  const t = parseTimestamp(iso);
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(d / 365)}y ago`;
}

export function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

export interface OwnerSummary {
  name: string;
  initials: string;
  color: PastelColor;
  email: string | null;
  isMine: boolean;
  isOrphaned: boolean;
}

export function ownerFor(link: Link, currentUser: User | null): OwnerSummary {
  const isOrphaned = link.created_by == null;
  const isMine = !isOrphaned && currentUser ? link.created_by === currentUser.id : false;
  const email = link.creator_email;
  let name: string;
  if (isOrphaned) name = 'Deleted user';
  else if (isMine) name = 'You';
  else name = nameFromEmail(email);
  return {
    name,
    initials: isOrphaned ? '–' : initialsFromName(name),
    color: colorForString(email ?? String(link.created_by ?? 'orphan')),
    email,
    isMine,
    isOrphaned,
  };
}

/** Group flat link list by alias group: primary link with .alternates[] of slugs. */
export interface GroupedLink extends Link {
  alternates: string[];
}

export function groupByAlias(links: Link[]): GroupedLink[] {
  const byId = new Map<number, Link>();
  links.forEach(l => byId.set(l.id, l));
  const aliasesByPrimary = new Map<number, Link[]>();
  for (const l of links) {
    if (l.alias_of != null) {
      const list = aliasesByPrimary.get(l.alias_of) ?? [];
      list.push(l);
      aliasesByPrimary.set(l.alias_of, list);
    }
  }
  return links
    .filter(l => l.alias_of == null)
    .map(primary => ({
      ...primary,
      alternates: (aliasesByPrimary.get(primary.id) ?? []).map(a => a.slug),
    }));
}
